/**
 * Email Verification Page (DC-895)
 *
 * Handles email verification when user clicks the link from their email.
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/auth.css';

export function EmailVerification() {
  const { token } = useParams<{ token: string }>();
  const { verifyEmail } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setError('Invalid verification link');
        return;
      }

      const result = await verifyEmail(token);

      if (result.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setError(result.error || 'Verification failed');
      }
    };

    verify();
  }, [token, verifyEmail]);

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <div className="auth-logo-text">DATA COMPASS</div>
          </div>

          {/* Loading state */}
          {status === 'loading' && (
            <>
              <div className="auth-loading">
                <div className="auth-loading-spinner" />
                <p>Verifying your email...</p>
              </div>
            </>
          )}

          {/* Success state */}
          {status === 'success' && (
            <>
              <div className="auth-success" style={{ marginBottom: '1.5rem' }}>
                <span>✓</span>
                Email verified!
              </div>

              <h1 className="auth-title">You're all set</h1>

              <p style={{ color: 'var(--muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
                Your email has been verified. You can now sign in to your account.
              </p>

              <Link
                to="/login"
                className="auth-button"
                style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
              >
                Sign In
              </Link>
            </>
          )}

          {/* Error state */}
          {status === 'error' && (
            <>
              <div className="auth-error" style={{ marginBottom: '1.5rem' }}>
                <span className="auth-error-icon">!</span>
                {error || 'Verification failed'}
              </div>

              <h1 className="auth-title">Verification failed</h1>

              <p style={{ color: 'var(--muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
                This verification link may be invalid or expired.
                Please try registering again or contact support.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Link
                  to="/register"
                  className="auth-button"
                  style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
                >
                  Register Again
                </Link>
                <Link
                  to="/login"
                  className="auth-button auth-button-secondary"
                  style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
                >
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
